import { preserveUserReplyOrder } from "./userReplyOrder";
import type { IDAChatMessage } from "./chatMessageMapper";
import {
  appendOutputTruncationNotice,
  OUTPUT_TRUNCATED_NOTICE_KEY,
} from "./outputTruncation";

function getLiveDocumentExecutionState(
  execution: NonNullable<IDAChatMessage["toolExecutions"]>[number],
) {
  if (
    execution.name !== "generate_document" &&
    execution.name !== "generate_presentation"
  ) {
    return null;
  }
  const result = execution.result as Record<string, unknown> | undefined;
  const hasDocumentIdentity =
    typeof result?.document_id === "string" &&
    typeof result.document_title === "string";
  // A live terminal result must win over a history snapshot captured before
  // the per-job stream delivered its terminal frame.
  if (execution.status === "error" && execution.error !== "Cancelled") {
    return { result, priority: 4 };
  }
  if (execution.status === "completed" && hasDocumentIdentity) {
    return { result, priority: 4 };
  }
  if (execution.status === "running" && typeof result?.job_id === "string") {
    return { result, priority: 2 };
  }
  return null;
}

function retainLiveToolExecutions(
  current: IDAChatMessage,
  fetched: IDAChatMessage,
): IDAChatMessage {
  const cancelled = new Set(
    (current.toolExecutions ?? [])
      .filter(
        (execution) =>
          execution.status === "error" && execution.error === "Cancelled",
      )
      .map((execution) => execution.id),
  );
  const currentByExecutionId = new Map(
    (current.toolExecutions ?? []).map((execution) => [
      execution.id,
      execution,
    ]),
  );
  let changed = false;
  const toolExecutions = fetched.toolExecutions?.map((execution) => {
    if (execution.status === "running" && cancelled.has(execution.id)) {
      changed = true;
      return { ...execution, status: "error" as const, error: "Cancelled" };
    }

    const live = currentByExecutionId.get(execution.id);
    const liveState = live && getLiveDocumentExecutionState(live);
    const fetchedState = getLiveDocumentExecutionState(execution);
    if (
      !live ||
      !liveState ||
      (fetchedState?.priority ?? 0) > liveState.priority ||
      ((fetchedState?.priority ?? 0) === liveState.priority &&
        liveState.priority < 4)
    ) {
      return execution;
    }
    const liveResult = liveState.result;
    const hasDocumentIdentity =
      typeof liveResult?.document_id === "string" &&
      typeof liveResult.document_title === "string";

    const fetchedResult = execution.result as
      Record<string, unknown> | undefined;
    const result = {
      ...fetchedResult,
      ...(typeof liveResult?.job_id === "string"
        ? { background: true, job_id: liveResult.job_id }
        : {}),
      ...(hasDocumentIdentity
        ? {
            document_id: liveResult.document_id,
            document_title: liveResult.document_title,
          }
        : {}),
    };
    const resultUnchanged = Object.entries(result).every(
      ([key, value]) => fetchedResult?.[key] === value,
    );
    const statusFromLive =
      liveState.priority > (fetchedState?.priority ?? 0) ||
      (liveState.priority === 4 && fetchedState?.priority === 4);
    if (
      (!statusFromLive || execution.status === live.status) &&
      resultUnchanged &&
      (!statusFromLive || execution.error === live.error)
    ) {
      return execution;
    }

    changed = true;
    return {
      ...execution,
      status: statusFromLive ? live.status : execution.status,
      result,
      error: statusFromLive ? live.error : execution.error,
    };
  });

  if (!changed || !toolExecutions) return fetched;
  return {
    ...fetched,
    toolExecutions,
  };
}

/** Keep live document job identity/status when final-status refresh uses history rows. */
export function preserveLiveDocumentExecutions(
  liveMessages: IDAChatMessage[],
  fetchedMessages: IDAChatMessage[],
): IDAChatMessage[] {
  const liveExecutions = new Map(
    liveMessages.flatMap((message) =>
      (message.toolExecutions ?? []).map(
        (execution) => [execution.id, { message, execution }] as const,
      ),
    ),
  );

  return fetchedMessages.map((message) => {
    const currentExecutions = (message.toolExecutions ?? []).flatMap(
      (execution) => {
        const live = liveExecutions.get(execution.id);
        return live ? [live.execution] : [];
      },
    );
    if (currentExecutions.length === 0) return message;
    return retainLiveToolExecutions(
      { ...message, toolExecutions: currentExecutions },
      message,
    );
  });
}

function retainOutputTruncationNotice(
  current: IDAChatMessage,
  fetched: IDAChatMessage,
): IDAChatMessage {
  const notice = current.metadata?.[OUTPUT_TRUNCATED_NOTICE_KEY];
  if (typeof notice !== "string" || !notice) return fetched;
  const currentAnswer = current.content.endsWith(`\n\n${notice}`)
    ? current.content.slice(0, -notice.length - 2)
    : current.content === notice
      ? ""
      : current.content;
  const savedAnswer = fetched.content || currentAnswer;
  return {
    ...fetched,
    content: savedAnswer.includes(notice)
      ? savedAnswer
      : appendOutputTruncationNotice(savedAnswer, notice),
    metadata: { ...fetched.metadata, [OUTPUT_TRUNCATED_NOTICE_KEY]: notice },
  };
}

function getServerMessageIds(message: IDAChatMessage): number[] {
  if (
    Array.isArray(message.hubMessageIds) &&
    message.hubMessageIds.length > 0
  ) {
    return message.hubMessageIds;
  }

  if (typeof message.hubMessageId === "number") {
    return [message.hubMessageId];
  }

  if (/^\d+$/.test(message.id)) {
    return [parseInt(message.id, 10)];
  }

  return [];
}

function getFallbackSignature(message: IDAChatMessage): string {
  return `${message.role}:${message.content.trim()}`;
}

function getAssistantTurnId(message: IDAChatMessage): string | undefined {
  const assistantTurnId = message.metadata?.assistant_turn_id;
  return typeof assistantTurnId === "string" && assistantTurnId.length > 0
    ? assistantTurnId
    : undefined;
}

function areNumberArraysEqual(left: number[], right: number[]): boolean {
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
}

function areMessagesEquivalent(
  currentMessage: IDAChatMessage,
  fetchedMessage: IDAChatMessage,
): boolean {
  return (
    currentMessage.id === fetchedMessage.id &&
    currentMessage.role === fetchedMessage.role &&
    currentMessage.content === fetchedMessage.content &&
    currentMessage.hubMessageId === fetchedMessage.hubMessageId &&
    areNumberArraysEqual(
      getServerMessageIds(currentMessage),
      getServerMessageIds(fetchedMessage),
    ) &&
    JSON.stringify(currentMessage.toolCalls) ===
      JSON.stringify(fetchedMessage.toolCalls) &&
    JSON.stringify(currentMessage.toolExecutions) ===
      JSON.stringify(fetchedMessage.toolExecutions) &&
    JSON.stringify(currentMessage.citations) ===
      JSON.stringify(fetchedMessage.citations) &&
    JSON.stringify(currentMessage.metadata) ===
      JSON.stringify(fetchedMessage.metadata)
  );
}

// True when a message carries persisted enrichment that a duplicate replay
// may omit. Used by the dedupe pre-pass to prefer the enriched row when both
// entries share a serverId.
//
// IMPORTANT — keep this field set IN SYNC with the persist-only enrichment the
// backend attaches on the polled/loaded path but NOT on the live-streaming
// path. Today that is citations, `metadata._ida_llm_route_provenance`, and the
// top-level usage siblings (model / provider / prompt_tokens /
// completion_tokens / total_tokens). If a future backend enrichment is added
// that lands only on the persisted row, add it here too — otherwise the dedupe
// can keep an incomplete duplicate and drop the enriched one.
function messageHasPersistedEnrichment(message: IDAChatMessage): boolean {
  return (
    (Array.isArray(message.citations) && message.citations.length > 0) ||
    message.metadata?._ida_llm_route_provenance != null ||
    message.model != null ||
    message.provider != null ||
    message.prompt_tokens != null ||
    message.completion_tokens != null ||
    message.total_tokens != null
  );
}

// Pre-pass: collapse currentMessages where two entries share a hubMessageId.
//
// Background: a stream-time race between the auto-continue optimistic bubble
// and the polling-merge can leave two messages for the same server row in
// `currentMessages` — one from the post-`done` id rewrite (string id, e.g.
// "73253" with hubMessageId=73253) and one from a polling-merge that ran
// during the stream and added the BE row as a separate entry. Without this
// pre-pass, downstream `existingServerIdToIndex` would silently overwrite
// indices for the duplicate hubMessageId, leaving both messages in place
// indefinitely.
//
// Priority for picking the canonical entry when duplicates are found:
//   1. `isStreaming === true` — that's the live render target; dropping it
//      would freeze the UI mid-stream
//   2. carries persisted enrichment — citations/provenance/usage must win over
//      a blank duplicate. Without this, the index/content tiebreaks below can
//      keep the blank duplicate: `sortMessagesByCreatedAt` may place the streamed
//      bubble at a later index than the enriched row (their createdAt differ),
//      and "later index wins" then drops the enriched one.
//   3. later array index — captures the most recent updates (e.g. id rewrites)
//   4. longer content — final tiebreaker
function dedupeCurrentMessagesByServerId(
  currentMessages: IDAChatMessage[],
): IDAChatMessage[] {
  const canonicalIndexById = new Map<number, number>();

  currentMessages.forEach((message, index) => {
    const serverIds = getServerMessageIds(message);
    if (serverIds.length === 0) return;

    for (const serverId of serverIds) {
      const priorIndex = canonicalIndexById.get(serverId);
      if (priorIndex === undefined) {
        canonicalIndexById.set(serverId, index);
        continue;
      }
      const prior = currentMessages[priorIndex];
      const sameStreaming = !!message.isStreaming === !!prior.isStreaming;
      const messageRich = messageHasPersistedEnrichment(message);
      const priorRich = messageHasPersistedEnrichment(prior);
      const currentWins =
        (!!message.isStreaming && !prior.isStreaming) ||
        (sameStreaming && messageRich && !priorRich) ||
        (sameStreaming && messageRich === priorRich && index > priorIndex) ||
        (sameStreaming &&
          messageRich === priorRich &&
          index === priorIndex &&
          message.content.length > prior.content.length);
      if (currentWins) canonicalIndexById.set(serverId, index);
    }
  });

  const dropIndexes = new Set<number>();
  currentMessages.forEach((message, index) => {
    const serverIds = getServerMessageIds(message);
    if (serverIds.length === 0) return;
    const isCanonical = serverIds.some(
      (serverId) => canonicalIndexById.get(serverId) === index,
    );
    if (!isCanonical) dropIndexes.add(index);
  });

  if (dropIndexes.size === 0) return currentMessages;
  return currentMessages.filter((_, index) => !dropIndexes.has(index));
}

function getSortableTime(message: IDAChatMessage): number {
  const time = message.createdAt?.getTime?.();
  return typeof time === "number" && !Number.isNaN(time) ? time : 0;
}

// Stable chronological order by createdAt. Tiebreak (same instant) by
// hubMessageId then id for determinism; a message without a server id sorts
// after persisted rows at the same instant (optimistic/streaming bubbles stay
// trailing). The decorated original index is the final guard so equal keys keep
// insertion order regardless of the engine's sort stability.
function sortMessagesByCreatedAt(messages: IDAChatMessage[]): IDAChatMessage[] {
  return preserveUserReplyOrder(
    enforceServerOrder(messages),
    enforceServerOrder(sortByClientCreatedAt(messages)),
  );
}

// The server orders a conversation by raw Hub message id (see
// conversationMessageWindow), and a reload shows exactly that. A streamed
// bubble keeps its client-side createdAt (stream start) when the poll finds it
// equivalent to the persisted row, so a user message sent mid-stream (persisted
// with a lower id than the reply) sorted BEFORE the server's order only by
// createdAt and rendered after the reply. Messages that carry a server id are
// therefore re-laid, in server-id order, into the slots server-identified
// messages already occupy; messages without one (optimistic/streaming) and job
// cards keep their place.
function enforceServerOrder(sorted: IDAChatMessage[]): IDAChatMessage[] {
  const slots: number[] = [];
  const withServerId: { message: IDAChatMessage; serverId: number }[] = [];
  sorted.forEach((message, index) => {
    const ids = getServerMessageIds(message);
    if (ids.length === 0) return;
    slots.push(index);
    withServerId.push({ message, serverId: Math.min(...ids) });
  });
  withServerId.sort((a, b) => a.serverId - b.serverId);

  const result = [...sorted];
  slots.forEach((slot, i) => {
    result[slot] = withServerId[i].message;
  });
  return result;
}

function sortByClientCreatedAt(messages: IDAChatMessage[]): IDAChatMessage[] {
  return messages
    .map((message, index) => ({ message, index }))
    .sort((a, b) => {
      const timeDiff = getSortableTime(a.message) - getSortableTime(b.message);
      if (timeDiff !== 0) return timeDiff;

      const aHub = a.message.hubMessageId ?? Number.POSITIVE_INFINITY;
      const bHub = b.message.hubMessageId ?? Number.POSITIVE_INFINITY;
      if (aHub !== bHub) return aHub - bHub;

      if (a.message.id !== b.message.id) {
        return a.message.id < b.message.id ? -1 : 1;
      }
      return a.index - b.index;
    })
    .map((entry) => entry.message);
}

function messagesShareIdentity(
  left: IDAChatMessage,
  right: IDAChatMessage,
): boolean {
  if (left.id === right.id) return true;

  const leftAssistantTurnId = getAssistantTurnId(left);
  if (
    leftAssistantTurnId &&
    leftAssistantTurnId === getAssistantTurnId(right)
  ) {
    return true;
  }

  const rightIds = new Set(getServerMessageIds(right));
  return getServerMessageIds(left).some((id) => rightIds.has(id));
}

/**
 * Keep already-rendered completion cards at the conversation boundary where
 * their result was first read out.
 *
 * Completion cards are inserted locally immediately before the automatic
 * assistant follow-up. A later poll replaces that optimistic follow-up with
 * its persisted row, whose server timestamp can predate the card's local
 * timestamp. A plain chronological sort then moves the card below the
 * follow-up (and can keep moving it as more rows arrive).
 *
 * Preserve each card relative to its nearest surviving neighbour. This is a
 * transcript-order anchor, not a request anchor or a screen/scroll coordinate.
 * Ordinary messages remain chronologically sorted, and cards that were not
 * already present still keep their fetched order.
 */
function preserveJobCompletionPositions(
  currentMessages: IDAChatMessage[],
  orderedMessages: IDAChatMessage[],
): IDAChatMessage[] {
  const existingCardIds = new Set(
    currentMessages
      .filter((message) => message.role === "job_completion")
      .map((message) => message.id),
  );
  if (existingCardIds.size === 0) return orderedMessages;

  const orderedCardById = new Map(
    orderedMessages
      .filter(
        (message) =>
          message.role === "job_completion" && existingCardIds.has(message.id),
      )
      .map((message) => [message.id, message]),
  );
  let restored = orderedMessages.filter(
    (message) => !orderedCardById.has(message.id),
  );

  currentMessages.forEach((currentCard, currentIndex) => {
    if (currentCard.role !== "job_completion") return;
    const card = orderedCardById.get(currentCard.id);
    if (!card) return;

    const nextNeighbour = currentMessages
      .slice(currentIndex + 1)
      .find((message) => message.role !== "job_completion");
    const previousNeighbour = currentMessages[currentIndex - 1];

    const nextIndex = nextNeighbour
      ? restored.findIndex((message) =>
          messagesShareIdentity(message, nextNeighbour),
        )
      : -1;
    if (nextIndex >= 0) {
      restored = [
        ...restored.slice(0, nextIndex),
        card,
        ...restored.slice(nextIndex),
      ];
      return;
    }

    const previousIndex = previousNeighbour
      ? restored.findIndex((message) =>
          messagesShareIdentity(message, previousNeighbour),
        )
      : -1;
    const insertionIndex =
      previousIndex >= 0
        ? previousIndex + 1
        : Math.min(currentIndex, restored.length);
    restored = [
      ...restored.slice(0, insertionIndex),
      card,
      ...restored.slice(insertionIndex),
    ];
  });

  return restored;
}

/**
 * Reconcile selected-conversation history with rows received or updated after
 * selection. Pre-selection ids exclude rows from the previously selected
 * conversation. A live row stays authoritative unless fetched history extends
 * its content, indicating that persistence is newer than the stream snapshot.
 */
export function mergeConversationHistoryWithLiveMessages(
  currentMessages: IDAChatMessage[],
  fetchedHistory: IDAChatMessage[],
  messageIdsBeforeSelection: ReadonlySet<string>,
  messagesAtSelectionById: ReadonlyMap<string, IDAChatMessage>,
): IDAChatMessage[] {
  const liveMessages = currentMessages.filter(
    (message) => !messageIdsBeforeSelection.has(message.id),
  );
  const merged = mergeConversationMessages(liveMessages, fetchedHistory);
  const streamUpdatesByTurnId = new Map<string, IDAChatMessage>();
  liveMessages.forEach((message) => {
    const turnId = getAssistantTurnId(message);
    const messageAtSelection = messagesAtSelectionById.get(message.id);
    if (
      turnId &&
      (message.isStreaming ||
        !messageAtSelection ||
        messageAtSelection.isStreaming !== message.isStreaming ||
        !areMessagesEquivalent(messageAtSelection, message))
    ) {
      streamUpdatesByTurnId.set(turnId, message);
    }
  });

  return merged.map((message) => {
    const turnId = getAssistantTurnId(message);
    const streamUpdatedMessage = turnId
      ? streamUpdatesByTurnId.get(turnId)
      : undefined;
    if (!streamUpdatedMessage) return message;

    const historyContinuesLiveContent =
      message.content.length > streamUpdatedMessage.content.length &&
      message.content.startsWith(streamUpdatedMessage.content);
    if (historyContinuesLiveContent && !streamUpdatedMessage.isStreaming) {
      return message;
    }

    return {
      ...message,
      ...streamUpdatedMessage,
      id: streamUpdatedMessage.id,
      hubMessageId: message.hubMessageId ?? streamUpdatedMessage.hubMessageId,
      content: historyContinuesLiveContent
        ? message.content
        : streamUpdatedMessage.content,
      isStreaming: streamUpdatedMessage.isStreaming,
      metadata: {
        ...message.metadata,
        ...streamUpdatedMessage.metadata,
      },
    };
  });
}

export function mergeConversationMessages(
  rawCurrentMessages: IDAChatMessage[],
  fetchedMessages: IDAChatMessage[],
): IDAChatMessage[] {
  if (rawCurrentMessages.length === 0) {
    return [...fetchedMessages];
  }

  const currentMessages = dedupeCurrentMessagesByServerId(rawCurrentMessages);

  const existingServerIdToIndex = new Map<number, number>();
  const existingAssistantTurnIdToIndex = new Map<string, number>();
  const optimisticMessageIndexesBySignature = new Map<string, number[]>();

  currentMessages.forEach((message, index) => {
    const serverIds = getServerMessageIds(message);
    if (serverIds.length > 0) {
      serverIds.forEach((serverId) => {
        existingServerIdToIndex.set(serverId, index);
      });
    } else {
      const signature = getFallbackSignature(message);
      const optimisticIndexes =
        optimisticMessageIndexesBySignature.get(signature) ?? [];
      optimisticIndexes.push(index);
      optimisticMessageIndexesBySignature.set(signature, optimisticIndexes);
    }
    const assistantTurnId = getAssistantTurnId(message);
    if (assistantTurnId) {
      existingAssistantTurnIdToIndex.set(assistantTurnId, index);
    }
  });

  const replacementsByIndex = new Map<number, IDAChatMessage>();
  const newServerMessages: IDAChatMessage[] = [];

  fetchedMessages.forEach((message) => {
    const serverIds = getServerMessageIds(message);
    const assistantTurnId = getAssistantTurnId(message);
    const turnMatchingIndex = assistantTurnId
      ? existingAssistantTurnIdToIndex.get(assistantTurnId)
      : undefined;
    if (serverIds.length > 0) {
      const matchingIndex =
        serverIds
          .map((serverId) => existingServerIdToIndex.get(serverId))
          .find((index): index is number => index !== undefined) ??
        turnMatchingIndex;

      if (matchingIndex !== undefined) {
        const currentMessage =
          replacementsByIndex.get(matchingIndex) ??
          currentMessages[matchingIndex];

        serverIds.forEach((serverId) => {
          existingServerIdToIndex.set(serverId, matchingIndex);
        });
        if (assistantTurnId) {
          existingAssistantTurnIdToIndex.set(assistantTurnId, matchingIndex);
        }

        const reconciled = retainLiveToolExecutions(
          currentMessage,
          retainOutputTruncationNotice(currentMessage, message),
        );
        if (!areMessagesEquivalent(currentMessage, reconciled)) {
          replacementsByIndex.set(matchingIndex, reconciled);
        }

        return;
      }

      // No existing serverId match. Prefer reconciling with an optimistic
      // bubble IN PLACE — replace it at its own index so it keeps its
      // chronological position AND gains the server id (hubMessageId), instead
      // of dropping the optimistic and tail-appending the server row (which
      // duplicated/re-ordered the just-sent user message).
      const signature = getFallbackSignature(message);
      const optimisticIndexes =
        optimisticMessageIndexesBySignature.get(signature) ?? [];
      const optimisticIndex = optimisticIndexes.shift();
      if (optimisticIndex !== undefined) {
        const optimisticMessage = currentMessages[optimisticIndex];
        // Keep the client's ordering timestamp while the optimistic turn is
        // reconciled with its persisted identity. The server-created user row
        // can carry a timestamp a few milliseconds after the already-appended
        // streaming assistant placeholder; adopting it here makes the global
        // chronological sort temporarily place the assistant above the user.
        // The persisted row still supplies every authoritative field and ID.
        replacementsByIndex.set(
          optimisticIndex,
          optimisticMessage.role === "user"
            ? { ...message, createdAt: optimisticMessage.createdAt }
            : retainLiveToolExecutions(optimisticMessage, message),
        );
        serverIds.forEach((serverId) => {
          existingServerIdToIndex.set(serverId, optimisticIndex);
        });
        if (optimisticIndexes.length === 0) {
          optimisticMessageIndexesBySignature.delete(signature);
        } else {
          optimisticMessageIndexesBySignature.set(signature, optimisticIndexes);
        }
        return;
      }

      serverIds.forEach((serverId) => {
        existingServerIdToIndex.set(
          serverId,
          currentMessages.length + newServerMessages.length,
        );
      });
      if (assistantTurnId) {
        existingAssistantTurnIdToIndex.set(
          assistantTurnId,
          currentMessages.length + newServerMessages.length,
        );
      }
      newServerMessages.push(message);

      return;
    }

    const signature = getFallbackSignature(message);
    if (optimisticMessageIndexesBySignature.has(signature)) {
      return;
    }
    newServerMessages.push(message);
  });

  const retainedMessages = currentMessages.map(
    (message, index) => replacementsByIndex.get(index) ?? message,
  );

  const merged = [...retainedMessages, ...newServerMessages];

  // Order by createdAt so a late-fetched server row (older timestamp) lands in
  // its chronological position instead of the tail. Stable: equal timestamps
  // keep insertion order (streaming-append order is preserved).
  const ordered = sortMessagesByCreatedAt(merged);

  // Always run the post-merge collapse pass (Case B Rules 2 + 3): even if
  // the merge itself didn't add or replace anything, the input
  // currentMessages array may contain bubbles that need collapsing. If
  // nothing actually changed after chronological ordering and card-position
  // restoration, return the original `rawCurrentMessages`
  // reference so the caller's stable-reference short-circuit
  // (`mergedMessages === current`) skips the setMessages re-render.
  const collapsed = preserveJobCompletionPositions(
    currentMessages,
    collapseRedundantBubbles(ordered),
  );
  const noMergeChanges =
    newServerMessages.length === 0 && replacementsByIndex.size === 0;
  const noPrePassDrops = currentMessages === rawCurrentMessages;
  const noFinalChanges =
    collapsed.length === rawCurrentMessages.length &&
    collapsed.every((message, index) => message === rawCurrentMessages[index]);
  if (noMergeChanges && noPrePassDrops && noFinalChanges) {
    return rawCurrentMessages;
  }
  return collapsed;
}

// Case C observability counter — emitted whenever the dedupe pass drops at
// least one bubble (i.e. visible-overlap was about to surface). One-line
// structured `console.warn` with a stable prefix so monitoring can scrape it
// from browser logs / sourcemap-aware error reporters. Future: wire to a real
// telemetry endpoint when one lands.
//
// Also emits when the same `tool_execution.id` is rendered against more than
// one message in a single conversation (separate `dup_chip_render` mode).
function emitDupChipRender(
  reason:
    | "merge_collapse_hub_id_containment"
    | "merge_collapse_prefix_overlap"
    | "user_message_prefix_overlap"
    | "tool_execution_id_collision",
  details: Record<string, unknown>,
): void {
  // Structured shape: `[ida-telemetry] dup_chip_render reason=<r> ...`. Stable
  // text prefix lets log scrapers filter without parsing JSON. Severity is
  // `warn` so it shows up in DevTools and reaches any reporter that captures
  // warnings; not `error` because the dedupe rules HANDLE the dup — the
  // counter is a regression early-warning, not a user-facing failure.
  console.warn("[ida-telemetry] dup_chip_render", {
    reason,
    timestamp: new Date().toISOString(),
    ...details,
  });
}

/**
 * Case B Rules 2 + 3 (post-merge dedupe pass).
 *
 * Rule 2 — `hubMessageIds[]` containment:
 *   The chatMessageMapper consolidates contiguous BE assistant rows into one
 *   bubble whose `hubMessageIds[]` is the union of constituent ids. After an
 *   auto-continue lifecycle, an additional optimistic-then-rewritten bubble
 *   may exist with a SCALAR `hubMessageId` matching one of the entries in the
 *   consolidated bubble's array (e.g. consolidated bubble has
 *   `hubMessageIds=[73315, 73316]` and the auto-continue bubble has
 *   `hubMessageId=73316`). Such scalar-only bubbles are duplicate visual
 *   surfaces of content that the consolidated bubble already renders — drop
 *   them.
 *
 * Rule 3 — Prefix-overlap collapse (defense-in-depth):
 *   For adjacent same-role assistant bubbles where `next.content.startsWith(
 *   prev.content) && prev.content.length > 0`, drop `prev`. Catches legacy
 *   backend responses where the BE persists per-iteration cumulative rows
 *   without the `visible_in_chat` flag (older Hub / SDK versions). Only
 *   applies when both bubbles have `tool_executions` present (signal of
 *   multi-iteration shape) — avoids collapsing arbitrary repetitions.
 */
function collapseRedundantBubbles(
  messages: IDAChatMessage[],
): IDAChatMessage[] {
  const dropIndexes = new Set<number>();

  // Rule 2: hubMessageIds[] containment dedupe.
  // Build a map of id -> set of indexes whose `hubMessageIds[]` contains it
  // (i.e. consolidated bubbles that own that id as part of a cluster).
  const consolidatedOwnersById = new Map<number, Set<number>>();
  messages.forEach((message, index) => {
    const ids = message.hubMessageIds;
    if (!Array.isArray(ids) || ids.length < 2) return;
    for (const id of ids) {
      const owners = consolidatedOwnersById.get(id) ?? new Set<number>();
      owners.add(index);
      consolidatedOwnersById.set(id, owners);
    }
  });

  messages.forEach((message, index) => {
    if (dropIndexes.has(index)) return;
    if (message.role !== "assistant") return;
    if (typeof message.hubMessageId !== "number") return;
    // Only drop scalar-only bubbles whose hubMessageIds[] is just the singular.
    const idsArr = message.hubMessageIds ?? [];
    if (idsArr.length > 1) return;
    const owners = consolidatedOwnersById.get(message.hubMessageId);
    if (!owners) return;
    // If any OTHER bubble's consolidated array contains this id, drop us.
    for (const ownerIndex of owners) {
      if (ownerIndex !== index) {
        dropIndexes.add(index);
        emitDupChipRender("merge_collapse_hub_id_containment", {
          dropped_hub_message_id: message.hubMessageId,
          dropped_index: index,
          owner_index: ownerIndex,
        });
        return;
      }
    }
  });

  // Rule 3: prefix-overlap collapse for adjacent same-role assistant bubbles.
  // Walk pairs (i, i+1) and drop the earlier one when content is a prefix
  // shared with the later one. Skip when either bubble is already dropped.
  for (let i = 0; i < messages.length - 1; i += 1) {
    if (dropIndexes.has(i)) continue;
    const prev = messages[i];
    const next = messages[i + 1];
    if (dropIndexes.has(i + 1)) continue;
    if (prev.role !== "assistant" || next.role !== "assistant") continue;
    if (!prev.content || !next.content) continue;
    if (!next.content.startsWith(prev.content)) continue;
    if (next.content === prev.content) continue;
    // Multi-iteration shape signal: both have tool_executions. This is the
    // BE per-iteration cumulative-row pattern; safe to collapse.
    if (
      !Array.isArray(prev.toolExecutions) ||
      !Array.isArray(next.toolExecutions) ||
      prev.toolExecutions.length === 0 ||
      next.toolExecutions.length === 0
    ) {
      continue;
    }
    dropIndexes.add(i);
    emitDupChipRender("merge_collapse_prefix_overlap", {
      dropped_hub_message_id: prev.hubMessageId,
      dropped_index: i,
      retained_hub_message_id: next.hubMessageId,
      retained_index: i + 1,
    });
  }

  // Report, but never collapse, a local user bubble followed by an overlapping
  // persisted user row. Unlike assistant tool iterations, user text has no
  // shape signal that makes prefix collapse safe: two intentional sends may
  // legitimately overlap. This is the optimistic/polling identity window
  // implicated by #4020, made observable without mutating the transcript.
  for (let i = 0; i < messages.length - 1; i += 1) {
    const optimistic = messages[i];
    const persisted = messages[i + 1];
    if (optimistic.role !== "user" || persisted.role !== "user") continue;
    if (!optimistic.content || !persisted.content) continue;
    if (getServerMessageIds(optimistic).length > 0) continue;
    const persistedIds = getServerMessageIds(persisted);
    if (persistedIds.length === 0) continue;
    if (!persisted.content.startsWith(optimistic.content)) continue;

    emitDupChipRender("user_message_prefix_overlap", {
      optimistic_message_id: optimistic.id,
      optimistic_index: i,
      persisted_hub_message_id: persistedIds[0],
      persisted_index: i + 1,
    });
  }

  // Also surface tool-execution-id collisions: same id rendered against
  // multiple messages indicates a structural dup that the rules above didn't
  // catch (e.g., an unfamiliar shape that bypasses both Rule 2 and Rule 3).
  // Emit a separate counter mode so monitoring can distinguish.
  const toolExecutionIdsToMessageIndexes = new Map<string, number[]>();
  messages.forEach((message, index) => {
    if (dropIndexes.has(index)) return;
    if (!Array.isArray(message.toolExecutions)) return;
    for (const execution of message.toolExecutions) {
      if (!execution.id) continue;
      const owners = toolExecutionIdsToMessageIndexes.get(execution.id) ?? [];
      owners.push(index);
      toolExecutionIdsToMessageIndexes.set(execution.id, owners);
    }
  });
  for (const [executionId, owners] of toolExecutionIdsToMessageIndexes) {
    if (owners.length > 1) {
      emitDupChipRender("tool_execution_id_collision", {
        tool_execution_id: executionId,
        message_indexes: owners,
      });
    }
  }

  if (dropIndexes.size === 0) return messages;
  return messages.filter((_, index) => !dropIndexes.has(index));
}
